import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, CircularProgress } from '@mui/material';

// Helper for field validation
const isEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isPhone = (phone: string) => /^\d{10}$/.test(phone);

const Register: React.FC = () => {
  const [form, setForm] = useState({
    carrierName: '',
    carrierDBA: '',
    physicalAddressLine1: '',
    physicalAddressLine2: '',
    country: '',
    city: '',
    stateProvince: '',
    zipPostalCode: '',
    mailingAddressLine1: '',
    mailingAddressLine2: '',
    mailingCountry: '',
    mailingCity: '',
    mailingStateProvince: '',
    mailingZipPostalCode: '',
    programCode: '',
    carrierGroup: '',
    defaultUserRole: '',
    contactName: '',
    contactEmail: '',
    contactTelephone: '',
    contactName2: '',
    contactEmail2: '',
    contactTelephone2: '',
    contactName3: '',
    contactEmail3: '',
    contactTelephone3: '',
    initalCardCountRequested: '',
    initalUnitTagCountRequested: '',
    initalTrailerTagCountRequested: '',
    dotNum: '',
    mcNum: '',
    federalId: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const validate = () => {
    if (!form.carrierName || form.carrierName.length > 40) return 'Carrier Name is required (max 40 chars).';
    if (!form.physicalAddressLine1 || form.physicalAddressLine1.length > 60) return 'Physical Address Line 1 is required (max 60 chars).';
    if (!form.country || form.country.length !== 3) return 'Country is required (USA, CAN, MEX, 3 letters).';
    if (!form.city || form.city.length > 20) return 'City is required (max 20 chars).';
    if (!form.stateProvince || form.stateProvince.length > 2) return 'State/Province is required (2 chars).';
    if (!form.zipPostalCode || form.zipPostalCode.length > 9) return 'ZIP/Postal Code is required (max 9 chars).';
    if (!form.programCode || form.programCode.length > 8) return 'Program Code is required (max 8 chars).';
    if (!form.contactName || form.contactName.length > 40) return 'Contact Name is required (max 40 chars).';
    if (!form.contactEmail || !isEmail(form.contactEmail) || form.contactEmail.length > 60) return 'Valid Contact Email is required (max 60 chars).';
    if (!form.contactTelephone || !isPhone(form.contactTelephone)) return 'Contact Telephone must be 10 digits.';
    if (form.initalCardCountRequested && isNaN(Number(form.initalCardCountRequested))) return 'Card count must be a number.';
    if (form.initalUnitTagCountRequested && isNaN(Number(form.initalUnitTagCountRequested))) return 'Unit tag count must be a number.';
    if (form.initalTrailerTagCountRequested && isNaN(Number(form.initalTrailerTagCountRequested))) return 'Trailer tag count must be a number.';
    // Optionally validate secondary contacts, mailing, etc.
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMsg('');
    const validation = validate();
    if (validation) {
      setError(validation);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('quikq_token');
      const response = await fetch('https://TVOJ-API-URL/carriers/newCarrierSetup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          ...form,
          initalCardCountRequested: form.initalCardCountRequested ? Number(form.initalCardCountRequested) : undefined,
          initalUnitTagCountRequested: form.initalUnitTagCountRequested ? Number(form.initalUnitTagCountRequested) : undefined,
          initalTrailerTagCountRequested: form.initalTrailerTagCountRequested ? Number(form.initalTrailerTagCountRequested) : undefined,
        }),
      });
      if (response.ok) {
        setMsg('Carrier registration successful!');
        setForm({
          carrierName: '', carrierDBA: '', physicalAddressLine1: '', physicalAddressLine2: '', country: '', city: '', stateProvince: '', zipPostalCode: '',
          mailingAddressLine1: '', mailingAddressLine2: '', mailingCountry: '', mailingCity: '', mailingStateProvince: '', mailingZipPostalCode: '',
          programCode: '', carrierGroup: '', defaultUserRole: '', contactName: '', contactEmail: '', contactTelephone: '', contactName2: '', contactEmail2: '', contactTelephone2: '', contactName3: '', contactEmail3: '', contactTelephone3: '', initalCardCountRequested: '', initalUnitTagCountRequested: '', initalTrailerTagCountRequested: '', dotNum: '', mcNum: '', federalId: '',
        });
      } else {
        const data = await response.json();
        setError(data.message || 'Carrier registration failed.');
      }
    } catch (err) {
      setError('Error communicating with the server.');
    }
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
      <Paper elevation={3} sx={{ p: 3, width: '100%', maxWidth: 600 }}>
        <Typography variant="h5" fontWeight={700} color="#1A5FB4" mb={2} align="center">
          Carrier Registration
        </Typography>
        <form onSubmit={handleSubmit}>
          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1} color="#1A5FB4">Carrier Information</Typography>
            <TextField label="Carrier Name*" name="carrierName" value={form.carrierName} onChange={handleChange} fullWidth margin="dense" required inputProps={{ maxLength: 40 }} />
            <TextField label="Carrier DBA" name="carrierDBA" value={form.carrierDBA} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 40 }} />
            <TextField label="Program Code*" name="programCode" value={form.programCode} onChange={handleChange} fullWidth margin="dense" required inputProps={{ maxLength: 8 }} />
            <TextField label="Carrier Group" name="carrierGroup" value={form.carrierGroup} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 40 }} />
            <TextField label="Default User Role" name="defaultUserRole" value={form.defaultUserRole} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 26 }} />
          </Paper>

          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1} color="#1A5FB4">Physical Address</Typography>
            <TextField label="Address Line 1*" name="physicalAddressLine1" value={form.physicalAddressLine1} onChange={handleChange} fullWidth margin="dense" required inputProps={{ maxLength: 60 }} />
            <TextField label="Address Line 2" name="physicalAddressLine2" value={form.physicalAddressLine2} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 60 }} />
            <TextField label="Country* (USA, CAN, MEX)" name="country" value={form.country} onChange={handleChange} fullWidth margin="dense" required inputProps={{ maxLength: 3 }} />
            <TextField label="State/Province*" name="stateProvince" value={form.stateProvince} onChange={handleChange} fullWidth margin="dense" required inputProps={{ maxLength: 2 }} />
            <TextField label="City*" name="city" value={form.city} onChange={handleChange} fullWidth margin="dense" required inputProps={{ maxLength: 20 }} />
            <TextField label="ZIP/Postal Code*" name="zipPostalCode" value={form.zipPostalCode} onChange={handleChange} fullWidth margin="dense" required inputProps={{ maxLength: 9 }} />
          </Paper>

          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1} color="#1A5FB4">Mailing Address (Optional)</Typography>
            <TextField label="Mailing Address Line 1" name="mailingAddressLine1" value={form.mailingAddressLine1} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 60 }} />
            <TextField label="Mailing Address Line 2" name="mailingAddressLine2" value={form.mailingAddressLine2} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 60 }} />
            <TextField label="Mailing Country" name="mailingCountry" value={form.mailingCountry} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 3 }} />
            <TextField label="Mailing State/Province" name="mailingStateProvince" value={form.mailingStateProvince} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 2 }} />
            <TextField label="Mailing City" name="mailingCity" value={form.mailingCity} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 20 }} />
            <TextField label="Mailing ZIP/Postal Code" name="mailingZipPostalCode" value={form.mailingZipPostalCode} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 9 }} />
          </Paper>

          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1} color="#1A5FB4">Contact Information</Typography>
            <TextField label="Contact Name*" name="contactName" value={form.contactName} onChange={handleChange} fullWidth margin="dense" required inputProps={{ maxLength: 40 }} />
            <TextField label="Contact Email*" name="contactEmail" value={form.contactEmail} onChange={handleChange} fullWidth margin="dense" required inputProps={{ maxLength: 60 }} />
            <TextField label="Contact Telephone* (10 digits)" name="contactTelephone" value={form.contactTelephone} onChange={handleChange} fullWidth margin="dense" required inputProps={{ maxLength: 10 }} />
            <TextField label="Contact Name 2" name="contactName2" value={form.contactName2} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 40 }} />
            <TextField label="Contact Email 2" name="contactEmail2" value={form.contactEmail2} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 60 }} />
            <TextField label="Contact Telephone 2 (10 digits)" name="contactTelephone2" value={form.contactTelephone2} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 10 }} />
            <TextField label="Contact Name 3" name="contactName3" value={form.contactName3} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 40 }} />
            <TextField label="Contact Email 3" name="contactEmail3" value={form.contactEmail3} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 60 }} />
            <TextField label="Contact Telephone 3 (10 digits)" name="contactTelephone3" value={form.contactTelephone3} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 10 }} />
          </Paper>

          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1} color="#1A5FB4">Request Details (Optional)</Typography>
            <TextField label="Initial Card Count" name="initalCardCountRequested" value={form.initalCardCountRequested} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 4 }} />
            <TextField label="Initial Unit Tag Count" name="initalUnitTagCountRequested" value={form.initalUnitTagCountRequested} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 4 }} />
            <TextField label="Initial Trailer Tag Count" name="initalTrailerTagCountRequested" value={form.initalTrailerTagCountRequested} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 4 }} />
            <TextField label="DOT Number" name="dotNum" value={form.dotNum} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 10 }} />
            <TextField label="MC Number" name="mcNum" value={form.mcNum} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 15 }} />
            <TextField label="Federal ID" name="federalId" value={form.federalId} onChange={handleChange} fullWidth margin="dense" inputProps={{ maxLength: 15 }} />
          </Paper>

          {error && <Typography color="error" fontSize={14} mt={2}>{error}</Typography>}
          {msg && <Typography color="green" fontSize={14} mt={2}>{msg}</Typography>}
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 3, bgcolor: '#1A5FB4' }} disabled={loading}>
            {loading ? <CircularProgress size={22} /> : 'Register Carrier'}
          </Button>
        </form>
        <Typography mt={2} fontSize={14} align="center">
          Already have an account? <a href="/login" style={{ color: '#1A5FB4', textDecoration: 'none' }}>Login</a>
        </Typography>
      </Paper>
    </Box>
  );
};

export default Register; 